import React, { useState, useEffect, cloneElement, Children } from 'react';
import { navigate } from 'gatsby';
import { MDXProvider } from '@mdx-js/react';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import { getUrlParams } from '@wec-core/helpers';
import DefaultLayout from '../layout/DefaultLayout';
import {
  ProductListingCategory,
  CardCatalog,
  TabGroup,
  Banner,
  ThumbnailList,
  CardList,
  Carousel,
  SpeedometerList,
  PageSection,
  RichText,
  PaddingController,
  ColumnController,
  FaqList,
  SingtelButton,
  TextWithIcon,
  Notification,
  CardComponent,
  AnchorLink,
} from '@singtel-main/widgets';
import { api } from '@detox/actions';
import Seo from '../components/SEO';
import { safeParse } from '../helpers/json';
import ChildRenderer from '../components/ChildRenderer';

const checkLoginApi = api.onlineApi.checkLogin;

const getShortcodes = async path => {
  const { shortCodes } = await import(`../externalPageConfigs/${path}`);
  return shortCodes;
};

const MarketingPageTemplate = props => {
  const { pageContext } = props;
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [showFooter, setShowFooter] = useState(true);
  const [modules, setModules] = useState(shortcodes);

  const { aemPageInfo: stringPageInfo } = pageContext;
  const pageInfo = safeParse(stringPageInfo);

  useEffect(() => {
    const { header = 'true', footer = 'true' } = getUrlParams();
    setShowHeader(header === 'true');
    setShowFooter(footer === 'true');
  }, []);

  useEffect(() => {
    if (pageContext.project) {
      getShortcodes(pageContext.jsxPath).then(item => {
        setModules(prev => ({ ...prev, ...item }));
      });
    }
  }, []);

  useEffect(() => {
    const checkLogin = async () => {
      try {
        const result = await checkLoginApi();
        setIsUserLoggedIn(result.isLoggedIn);
      } catch (e) {
        // Failed to check login, default isLoggedInTo false
        setIsUserLoggedIn(false);
      }
    };

    checkLogin();
  }, []);

  useEffect(() => {
    if (pageInfo.redirectTarget) {
      navigate(pageInfo.redirectTarget);
    }
  }, [navigate]);

  return (
    typeof window !== 'undefined' &&
    !pageInfo.redirectTarget && (
      <DefaultLayout
        isUserLoggedIn={isUserLoggedIn}
        showHeader={showHeader}
        showFooter={showFooter}
        showAppBanner={!(pageInfo.hideAppBanner === 'true')}
      >
        <MDXProvider components={modules}>
          <MDXRenderer>{pageContext.jsx}</MDXRenderer>
        </MDXProvider>
      </DefaultLayout>
    )
  );
};

export default MarketingPageTemplate;

export const Head = ({ pageContext, ...props }) => {
  return <Seo stringPageInfo={pageContext.aemPageInfo} {...props} />;
};

const shortcodes = {
  ProductListingCategory,
  CardCatalog,
  Banner,
  Carousel: ({ autoPlay, ...props } = {}) => {
    return <Carousel autoplay={autoPlay} lazyLoad={true} {...props} />;
  },
  ThumbnailList,
  CardList,
  SpeedometerList,
  PageSection,
  RichText,
  TabWrapper: ({ children }) => {
    return (
      <>
        {Array.isArray(children) && Children.count(children) > 0
          ? cloneElement(children[0], {
              tabs: Children.map(children, child => child.props),
            })
          : cloneElement(children, { tabs: [children.props] })}
      </>
    );
  },
  Tab: ({ tabs }) => {
    const [active, setActive] = useState(0);
    const handleOnSelect = index => setActive(index);
    const renderChildren = active => <>{tabs[active].children}</>;

    return (
      <TabGroup tabs={tabs} selectedIndex={active} onSelect={handleOnSelect}>
        {renderChildren(active)}
      </TabGroup>
    );
  },
  PaddingController,
  ColumnController: ({ children, ...props }) => {
    return <ColumnController {...props}>{children}</ColumnController>;
  },
  FaqList: ({ faqTitle, ...props }) => {
    return <FaqList heading={faqTitle} {...props} />;
  },
  SingtelButton,
  TextWithIcon,
  Notification,
  ChildRenderer,
  CardComponent,
  AnchorLink,
};
